"use client";

import posthog from "posthog-js";

import { analyticsConfig } from "./config";

/**
 * Emit when a buyer submits the inquiry form on a property detail page.
 * This replaces the old waitlist-submitted event as our primary
 * conversion signal — inquiry is buying intent on a specific property
 * (much stronger than "I'm interested generally"), so it's the right
 * Meta Pixel Lead event for ad optimization.
 *
 * `propertySlug` is included so we can track which listings convert.
 */
export function trackInquirySubmitted(properties: {
  propertySlug: string;
  hasCedula?: string;
  financingPath?: string;
}): void {
  if (analyticsConfig.posthog.key) {
    posthog.capture("inquiry_submitted", properties);
  }

  if (analyticsConfig.metaPixel.id && typeof window !== "undefined") {
    const fbq = (window as MetaPixelWindow).fbq;
    if (typeof fbq === "function") {
      // Meta's standard Lead event — used by their delivery
      // optimization. content_name surfaces the listing in Events
      // Manager so we can see which listings drive conversions.
      fbq("track", "Lead", {
        content_name: properties.propertySlug,
        content_category: "property_inquiry",
      });
    }
  }
}

/**
 * Emit when a visitor completes a calculator scenario. Properties are
 * useful market intelligence — distribution of savings + monthly budget
 * tells us who's actually engaging and at what budget tier.
 */
export function trackCalculatorUsed(properties: {
  savings: number;
  monthly: number;
  resulting_price: number;
  binding_constraint: "savings" | "monthly";
}): void {
  if (analyticsConfig.posthog.key) {
    posthog.capture("calculator_used", properties);
  }
}

/**
 * Emit when a visitor shares an article. Tells us which channels are
 * actually driving distribution and which articles are share-worthy.
 */
export function trackArticleShared(properties: {
  slug: string;
  channel: "whatsapp" | "facebook" | "copy";
}): void {
  if (analyticsConfig.posthog.key) {
    posthog.capture("article_shared", properties);
  }
}

/**
 * Emit when a visitor casts a vote in an inline poll. `poll` is the
 * stable poll id (e.g. "motivation"); `option` is the chosen option id.
 * Used to learn which framings of a question resonate most.
 */
export function trackPollVoted(properties: {
  poll: string;
  option: string;
}): void {
  if (analyticsConfig.posthog.key) {
    posthog.capture("poll_voted", properties);
  }
}

interface MetaPixelWindow extends Window {
  fbq?: (...args: unknown[]) => void;
}
